import { Injectable } from "@angular/core";
import { ConnectureAgentPermissionResponse, ConnectureAgentPermissionPayload, CONNECTURE_CARRIER_ID, CONNECTURE_MEDIGAP_TYPES, CONNECTURE_MEDICARE_TYPES, CONNECTURE_DSNP_TYPES, CONNECTURE_ANCILLARY_TYPES } from "../../interfaces/connecture-sso.interface";
import { BaseAgency, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, UserInfo, AGENCY_GOOD_TO_SELL, AGENCY_NOT_GOOD_TO_SELL, UserInfoStateStructure } from "@ptl/account-management";

import { CommonUtilityService } from "../../../../shared/utility-services/common-utility.service";
import { brandValues } from "@ptl/brand";
import { PtlNotificationService, PtlNotificationStyle } from "@ptl/global-components";
import { ConnectureEnrollService } from "./connecture-enroll-sso.service";
import { HttpErrorResponse } from "@angular/common/http";
import { SSOExtraDataRequestPayload, SSOPrimerRequestPayload } from "../../interfaces/sso-primer.interface";
import { ProducerService } from "../../producer.service";
import { PERSONAL_INFO_STATE_NAME } from "../../../../shared/reducers/personalInfo.reducer";
import { ZIP_COUNTY_STATE_NAME } from "../../../demographics/zip-county-modal/reducers/zipCounty.reducer";
import { PrePopulatedDataStateStructure } from "../../../shared/components/configs/interfaces/prePopulatedDataStateStructure.interface";
import { PRE_POPULATED_DATA_STATE_NAME } from "../../../shared/components/configs/reducers/prePopulatedData.reducer";
import { PersonalInfoStateStructure } from "../../../shared/interfaces/personalInfoStateStructure.interface";
import { ZipCountyStateStructure } from "../../../shared/interfaces/zipCountyStateStructure.interface";
import { ReducerUtilityService } from "src/app/shared/utility-services/reducer-utility.service";

@Injectable({
  providedIn: 'root'
})
export class ConnectureEnrollPermissionsService {

  constructor(
    private ptlNotificationService: PtlNotificationService,
    private connectureEnrollService: ConnectureEnrollService,
    private commonUtility: CommonUtilityService,
    private producerService: ProducerService,
    private entitlementHelper: EntitlementsHelperService,
    private reducerUtilityService: ReducerUtilityService
  ) { }

  showSSOError() {
    this.ptlNotificationService.open({
      title: 'Error',
      content: 'An error occurred during the SSO, please try again later or contact support.',
      primaryButtonText: 'OK',
      notificationStyle: PtlNotificationStyle.DANGER
    });
  }
  
  /* 
  * The outline of the connecture enrollment SSO is as follows:
  *
  * Step 1: Search for an existing Agent. If the Agent exists, skip to Step 3
  * 
  * Step 2: When no Agent exists, we have to create the Agent
  * 
  * Step 3: Search for an existing Agent permissions profile. If no profile exists, skip to Step 6
  * 
  * Step 4: When a profile exists, we must check to see if all of the correct values exist based on the
  *         current user's agency list and user information.  If everything matches, skip to Step 7
  * 
  * Step 5: When a profile exists but does not match our information exactly, we have to remove all the
  *         permissions for this user because there is no update functionality.
  * 
  * Step 6: When no permissions profile exists, or data was mismatched and removed, we have to create the agent permissions profile
  * 
  * Step 7: Connecture requires some additional agent information to be applied via a REST call
  * 
  * Step 8: We have to prime the SSO with some additional values, so make this API call last to update the data on the backend
  * 
  * Step 9: Trigger the SSO handler
  */

  async setupConnecturePermissionsForSSO(loginInfo: UserInfoStateStructure, lob: LINE_OF_BUSINESS_ENUM, isPlanChange: boolean): Promise<boolean> {
    return await this.findOrCreateAgent(loginInfo) &&
        await this.findOrCreateAgentPermissions(loginInfo.userInfo, loginInfo.userId, lob) &&
        await this.ssoExtraDataRequest(loginInfo.userInfo, loginInfo.userId, lob) &&
        await this.ssoPrimerRequest(loginInfo, lob, isPlanChange);
  }

  async findOrCreateAgent(loginInfo: UserInfoStateStructure): Promise<boolean> {
    try {
      await this.connectureEnrollService.searchForConnectureAgent(loginInfo.userId);

      return true;
    }
    catch(e) {
      if (e instanceof HttpErrorResponse && e?.status == 404) {
        return await this.createAgent(loginInfo);
      }
      
      console.error('Error searching for agent for sso', e);
    }

    return false;
  }

  async findOrCreateAgentPermissions(userInfo: UserInfo, userId: string, lob: LINE_OF_BUSINESS_ENUM): Promise<boolean> {
    try {
      let response = await this.connectureEnrollService.searchForConnectureAgentPermissions(userId);
      if (!response?.length || await this.removeAgentPermissions(userInfo, userId, response)) {
        return await this.createAgentPermissions(userInfo, userId);
      }

      return true;
    }
    catch (e) {
      console.error('Error searching for connecture agent permissions for sso', e);
    }

    return false;
  }

  // Due to Connecture not using the correct fields, we have to make this extra call to add
  // the necessary agent information.  This might be removable in the future if they fix their issues
  async ssoExtraDataRequest(userInfo: UserInfo, userId: string, lob: LINE_OF_BUSINESS_ENUM): Promise<boolean> {
    let selectedAgency = this.entitlementHelper.getSelectedAgency(lob);

    let ssoPayload: SSOExtraDataRequestPayload = {
      "AgentID": userId,
      "UserName": userId,
      "Password": null,
      "ExternalUserId": userId,
      "FirstName": userInfo.firstName,
      "LastName": userInfo.lastName,
      "EmailAddress": userInfo.email,
      "PhoneNumber": userInfo.telephone,
      "Extension": null,
      "Address1": userInfo.address, 
      "City": userInfo.city,
      "State": userInfo.state,
      "Zip": userInfo.zipCode,
      "AgentRole": "Enroller_Broker",
      "NPN": selectedAgency.npnNo || userInfo.npnNo || '1111111',
      "IsActive": 1,
      "ResetPasswordRequired": +userInfo.pwd_tempflag,
      "ExpirationDate": "2050-12-31T00:00:00",
      "AgencyID": selectedAgency.agencyId,
      "AgencyName": selectedAgency.agencyName?.substring(0, 50)
    }

    try{
      let response = await this.producerService.ssoExtraDataRequest(ssoPayload);
      
      // response should be the same as the payload, this should suffice as a simple verification
      if (response?.AgentID == userId) {
        return true;
      }

      console.error('extra data response invalid for connecture sso', response);
    }
    catch(e) {
      console.error('extra data request failed for connecture sso', e);
    }

    return false;
  }
  
  async ssoPrimerRequest(loginInfo: UserInfoStateStructure, lob: LINE_OF_BUSINESS_ENUM, isPlanChange: boolean): Promise<boolean> {
    let selectedAgency = this.entitlementHelper.getSelectedAgency(lob);

    let ssoPayload: SSOPrimerRequestPayload = {
        uid: loginInfo.userId,
        hmsaml124: selectedAgency.npnNo || loginInfo.userInfo.npnNo || '1111111',
        hmsaml126: selectedAgency.agencyId,
        hmsaml127: selectedAgency.agencyName,
        hmsaml138: loginInfo.userInfo.brokerRepType
    };

    if (isPlanChange) {
      const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState(PRE_POPULATED_DATA_STATE_NAME);
      const personalInfo: PersonalInfoStateStructure = this.reducerUtilityService.getPayloadState(PERSONAL_INFO_STATE_NAME);
      const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState(ZIP_COUNTY_STATE_NAME);
      Object.assign(ssoPayload, {
        hmsaml125: prePopulateData.prepopulatedFields.hicNumber, // MBI -- HIC NUMBER is the same as MBI
        hmsaml129: prePopulateData.prepopulatedFields.firstName, // BENEFICIARY_FIRST_NAME
        hmsaml130: prePopulateData.prepopulatedFields.lastName, // BENEFICIARY_LAST_NAME,
        hmsaml131: prePopulateData.prepopulatedFields.dob?.replaceAll('/',''), // BENEFICIARY_BIRTH_DATE, (MMDDYYYY)
        hmsaml132: personalInfo.addressLine1 || prePopulateData.prepopulatedFields.addressLine1, //BENEFICIARY_ADDRESS_LINE_1
        hmsaml133: zipCountyState.zipCode || prePopulateData.prepopulatedFields.zipCode, //BENEFICIARY_ZIP_CODE
        hmsaml134: personalInfo.city || prePopulateData.prepopulatedFields.city, //BENEFICIARY_CITY
        hmsaml135: zipCountyState.county?.state || prePopulateData.prepopulatedFields.state, //BENEFICIARY_STATE
        hmsaml136: prePopulateData.prepopulatedFields.gender?.toUpperCase() === 'M' ? '1' : '2', //BENEFICIARY_GENDER (1 = MALE, 2 = FEMALE),
        hmsaml137: prePopulateData.prepopulatedFields.hicNumber // HIC NUMBER
      });
    }

    try{
      let response = await this.producerService.ssoPrimerRequest(ssoPayload);
      if (response.status == 'true') {
        return true;
      }

      console.error('primer response invalid for connecture sso', response);
    }
    catch(e) {
      console.error('primer request failed for connecture sso', e);
    }

    return false;
  }

  private async createAgent(loginInfo: UserInfoStateStructure): Promise<boolean> {
    try {
      // AgentID, UserName, and ExternalUserID should be the same
      let agentPayload = {
        AgentID: loginInfo.userId,
        UserName: loginInfo.userId,
        ExternalUserId: loginInfo.userId,
        FirstName: loginInfo.userInfo.firstName,
        LastName: loginInfo.userInfo.lastName,
        EmailAddress: loginInfo.userInfo.email,
        PhoneNumber: loginInfo.userInfo.telephone,
        Address1: loginInfo.userInfo.address,
        City: loginInfo.userInfo.city,
        State: loginInfo.userInfo.state,
        Zip: loginInfo.userInfo.zipCode,
        AgentRole: 'Enroller_Broker',
        NPN: loginInfo.userInfo.npnNo || '1111111',
        IsActive: 1,
        ResetPasswordRequired: 0,
        ExpirationDate: '12-31-2050',
        AgencyID: '',
        AgencyName: ''
      };

      await this.connectureEnrollService.createConnectureAgent(agentPayload);

      return true;
    }
    catch(e) {
      console.error('Error creating connecture agent for sso', e);
    }
    
    return false;
  }

  /** 
   *  @description Checks the response of the permission payload to determine if we have any mismatched permissions based
   *  on agency details and plan year. If there is a mismatch, attempt to remove those permissions before returning true
   * 
   *  @returns true to indicate we need to create the user permissions
   *  @returns false to indicate we have the necessary permissions and do not need to call the create service
   */
  private async removeAgentPermissions(userInfo: UserInfo, userId: string, responsePermissions: ConnectureAgentPermissionResponse[]): Promise<boolean> {
    let actualPermissions = await this.buildActivePermissionPayload(userInfo);

    // If the response has no entries, there are no permissions so we must create them
    if (!responsePermissions?.length) {
      return true;
    }

    // Standardize and sort the permissions list by plan year and state. The order is important for comparing below
    let connecturePermissions = this.compilePermissionResponseToPayload(responsePermissions);
    connecturePermissions = connecturePermissions.sort(this.sortByPlanYearAndState);
    actualPermissions = actualPermissions.sort(this.sortByPlanYearAndState);

    // At this point connecturePermissions and actualPermissions should have the same entries in the same order if they match exactly
    if (actualPermissions.length === connecturePermissions.length) {
      for (let i = 0; i < actualPermissions.length; i++) {
        if (!this.isMatchingPermission(actualPermissions[i], connecturePermissions[i])) {
          await this.connectureEnrollService.removeConnectureAgentPermissionProfile(userId, connecturePermissions);

          return true;
        }
      }
    }
    else {
      await this.connectureEnrollService.removeConnectureAgentPermissionProfile(userId, connecturePermissions);

      return true;
    }

    return false;
  }

  private async createAgentPermissions(userInfo: UserInfo, userId: string): Promise<boolean> {
    try {
      let permissionPayload = await this.buildActivePermissionPayload(userInfo);

      if (!permissionPayload.length) {
        return true;
      }

      await this.connectureEnrollService.createConnectureAgentPermissionProfile(userId, permissionPayload);

      return true;
    }
    catch (e) {
      console.error('Error creating connecture agent permissions for sso', e);
    }

    return false;
  }

  /**
   * 
   * @description Builds a special payload for connecture permissions based on the user's current agency information.
   * NOTE: The term "state" in this function always refers to the 50 United States, not the redux state.
   */
  private async buildActivePermissionPayload(userInfo: UserInfo): Promise<ConnectureAgentPermissionPayload[]> {
    let permissionPayload: ConnectureAgentPermissionPayload[] = [];
    let yearOptions = this.commonUtility.getYearOptionsForAgentPermissions();
    let medicareStates = this.getAllUserRegions(AGENCY_GOOD_TO_SELL, LINE_OF_BUSINESS_ENUM.SENIOR);
    let medigapStates = this.getAllUserRegions(AGENCY_NOT_GOOD_TO_SELL, LINE_OF_BUSINESS_ENUM.SENIOR);
    let dsnpStates = this.getAllUserRegions(AGENCY_GOOD_TO_SELL, LINE_OF_BUSINESS_ENUM.DSNP);

    // All medciare states will have medigap by default. This ensures the medigap states contain all medicare states as well.
    medicareStates.forEach((medState) => {
      if (!medigapStates.includes(medState)) {
        medigapStates.push(medState);
      }
    });

    // First we add all the medicare goodToSell agencies to the payload
    medicareStates.forEach((state) => {
      yearOptions.forEach(year => {
        permissionPayload.push({
          CarrierID: CONNECTURE_CARRIER_ID,
          PlanYear: year.value,
          States: [state],
          PlanTypes: CONNECTURE_MEDICARE_TYPES,
          SNPTypes: [], // SNP types added with DSNP states below
          AgentProducerID: userInfo.npnNo || '1111111'
        });
      });
    });

    // Then, Update or add the SNP Types into the existing payload based on state value
    dsnpStates.forEach((state) => {
      let isAdded = false;
      permissionPayload.forEach((perm) => {
        if (perm.States.includes(state)) {
          isAdded = true;
          perm.SNPTypes = [1];
        }
      });

      if (!isAdded) {
        yearOptions.forEach(year => {
          permissionPayload.push({
            CarrierID: CONNECTURE_CARRIER_ID,
            PlanYear: year.value,
            States: [state],
            PlanTypes: CONNECTURE_DSNP_TYPES,
            SNPTypes: [1],
            AgentProducerID: userInfo.npnNo || '1111111'
          });
        });
      }
    });

    // Add the medigap plan types 
    medigapStates.forEach((state) => {
      permissionPayload.push({
        CarrierID: CONNECTURE_CARRIER_ID,
        PlanYear: null,
        States: [state],
        PlanTypes: CONNECTURE_MEDIGAP_TYPES,
        SNPTypes: [],  // Must be empty for these specific PlanTypes
        AgentProducerID: userInfo.npnNo || '1111111'
      });

      // Ancillary permissions are applied alongside medigap permissions (BEB plans)
      permissionPayload.push({
        CarrierID: CONNECTURE_CARRIER_ID,
        PlanYear: "2027",
        States: [state],
        PlanTypes: CONNECTURE_ANCILLARY_TYPES,
        SNPTypes: [],  // Must be empty for these specific PlanTypes
        AgentProducerID: userInfo.npnNo || '1111111'
      });
    });

    return permissionPayload;
  }


  private compilePermissionResponseToPayload(response: ConnectureAgentPermissionResponse[]): ConnectureAgentPermissionPayload[] {
    const map = new Map<string, ConnectureAgentPermissionPayload>();

    response.forEach(permission => {
      const key = `${permission.PlanYear}-${permission.State}`;

      if (map.has(key)) {
        const existing = map.get(key);

        if (!existing.PlanTypes.includes(permission.PlanType)) {
          existing.PlanTypes.push(permission.PlanType);
        }

        // any perm with a '1' value will give the whole state the permission flag
        // If the existing types do not contain a '1', we can overwrite with whatever is in the current perm
        if (existing.SNPTypes[0] != 1) {
          existing.SNPTypes = permission.SNPTypes;
        }
      }
      else {
        map.set(key, {
          CarrierID: permission.CarrierID,
          PlanYear: permission.PlanYear?.toString(),
          States: [permission.State],
          PlanTypes: [permission.PlanType],
          AgentProducerID: permission.AgentProducerID,
          SNPTypes: permission.SNPTypes
        });
      }
    });

    return Array.from(map.values());
  }

  private sortByPlanYearAndState(a, b) {
    if (!a.PlanYear && !b.PlanYear) {
      return a.States[0].localeCompare(b.States[0])
    }
    if (!a.PlanYear) {
      return 1;
    }

    return a.PlanYear.localeCompare(b.PlanYear) || a.States[0].localeCompare(b.States[0]);
  }

  private getAllUserRegions(goodToSellValue: number, lob: LINE_OF_BUSINESS_ENUM): string[] {
    let agencies = this.entitlementHelper.getAgenciesByLineOfBusiness(lob);
    let states = [];

    agencies.forEach((agency) => {
      if (this.isValidAgencyRegion(states, agency, goodToSellValue)) {
        states.push(agency.region);
      }
    });

    return states;
  }


  // Validate the agency region is an actual valid region as it is not guaranteed
  // NOTE: Agency "region" is actually a state value, and will not contain actual region information
  private isValidAgencyRegion(states: string[], agency: BaseAgency, goodToSellValue: number) {
    return agency?.region &&
      !states.includes(agency.region) &&
      brandValues.filter((brand) => brand.state === agency.region).length &&
      agency.goodToSell === goodToSellValue;
  }

  private isMatchingPermission(actualPerm, connecturePerm) {
    return actualPerm.AgentProducerID == connecturePerm.AgentProducerID &&
        actualPerm.CarrierID == connecturePerm.CarrierID &&
        JSON.stringify(actualPerm.PlanTypes) == JSON.stringify(connecturePerm.PlanTypes) &&
        actualPerm.PlanYear == connecturePerm.PlanYear &&
        actualPerm.States[0] == connecturePerm.States[0] &&
        actualPerm.SNPTypes[0] == connecturePerm.SNPTypes[0];
  }
}